import { useCallback } from 'react';
import { logger } from '~/scripts/logger';

export const waitForRecaptcha = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (typeof window !== 'undefined' && window.grecaptcha) {
      return resolve();
    }

    const timeout = setTimeout(() => {
      clearInterval(interval);
      reject(
        new Error(
          'Failed to load recaptcha in a reasonable amount of time. Please contact support@nerdydata.com.'
        )
      );
    }, 10000);

    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.grecaptcha) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve();
      }
    }, 300);
  });
};

// Core recaptcha functionality without hooks
export const executeRecaptcha = async (
  action: string,
  skipRecaptcha?: boolean
): Promise<string> => {
  await waitForRecaptcha();

  if (!window.grecaptcha) {
    throw new Error(
      'Failed to load recaptcha script. Please refresh the page or email support@nerdydata.com'
    );
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha!.ready(() => {
      if (skipRecaptcha) {
        return resolve('1');
      }
      window
        .grecaptcha!.execute(import.meta.env.VITE_RECAPTCHA_KEY!, {
          action,
        })
        .then(
          (token: string) => {
            resolve(token);
          },
          (error: unknown) => {
            logger.error('recaptcha error', error);
            reject(error);
          }
        );
    });
  });
};

// Hook wrapper for React components
export function useRecaptcha() {
  return useCallback(
    async (action: string): Promise<string> => {
      const skipRecaptcha = window.location.search.includes('recaptcha=1');
      return executeRecaptcha(action, skipRecaptcha);
    },
    [] // Only depend on the specific value we need
  );
}
